import { useUserControlsContext, useUserControlsLoaded } from '../../../web/app-home/src/contexts/UserControlsContext';

interface CurrentUser {
  /** The acting user's email (ownerEmail from the DB). Used by SettingsPanel and ActionQueuePage. */
  ownerEmail: string | null;
  /** Same value — kept for backward compat with ActionQueuePage's `user.ownerEmail ?? user.email` pattern. */
  email: string | null;
  /** Marketing role flag from /api/me (PlayhousePage gates its marketing view on this). */
  isMarketingUser: boolean;
}

interface UseCurrentUserResult {
  user: CurrentUser | null;
  isAdmin: boolean;
  isMarketingUser: boolean;
  roles: string[];
  isLoading: boolean;
}

/**
 * Simplified user context for mini-apps, derived from the shell's single
 * /api/me query (`UserControlsProvider`, which every mini-app route renders
 * inside). Until controls load, `user` is null, `isAdmin` false and `isLoading`
 * true. A failed or malformed /api/me never reaches here: the provider blocks
 * its children behind an error screen (fail closed).
 */
export function useCurrentUser(): UseCurrentUserResult {
  const controls = useUserControlsContext();
  const isLoaded = useUserControlsLoaded();

  const user: CurrentUser | null = isLoaded
    ? { ownerEmail: controls.email, email: controls.email, isMarketingUser: controls.isMarketingUser }
    : null;

  return {
    user,
    isAdmin: controls.isAdmin,
    isMarketingUser: controls.isMarketingUser,
    roles: controls.roles,
    isLoading: !isLoaded,
  };
}
